import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { YouTubePlayer } from '@angular/youtube-player';

import { YoutubePlayerService } from '@shared/modules/youtube-player/youtube-player.service';
import { defaultVideoRatio, VideoEmbedInfo } from '@shared/models/video-embed.model';

@Component({
  selector: 'zef-youtube-player',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  template: `
    <youtube-player [videoId]="video?.videoId" [width]="width" [height]="height" [playerVars]="playerVars" />
  `,
  styleUrls: ['./youtube-player.component.scss'],
  standalone: false,
})
export class YoutubePlayerComponent implements OnChanges, OnDestroy, OnInit {
  private yp = inject(YoutubePlayerService);

  @Input()
  video?: VideoEmbedInfo;

  @Input()
  language?: string;

  @Input()
  width: number = 640;

  @ViewChild(YouTubePlayer, { static: true })
  protected player: YouTubePlayer;

  @HostBinding('style.height.px')
  get hostHeight(): number {
    return this.height;
  }

  readonly defaultPlayerVars = {
    enablejsapi: 1,
    modestbranding: 1,
    playsinline: 1,
    rel: 0,
    showinfo: 0,
    iv_load_policy: 0,
  };

  protected height: number;

  protected playerVars: YT.PlayerVars;

  ngOnInit() {
    this.yp.register(this.player);
  }

  ngOnChanges(changes: SimpleChanges): void {
    const videoChanged = (changes.video && this.video) || (changes.language && this.language && this.video);

    if (videoChanged) {
      if (
        changes.video.firstChange ||
        this.video.startTime !== this.playerVars.start ||
        this.video.endTime !== this.playerVars.end ||
        (this.language && this.language !== this.playerVars.hl)
      ) {
        this.playerVars = {
          ...this.defaultPlayerVars,
          start: this.video.startTime,
          end: this.video.endTime,
          hl: this.language,
        };
      }
    }

    if (videoChanged || (changes.width && this.width)) {
      const height = this.width / (this.video.aspectRatio || defaultVideoRatio);

      if (height !== this.height) {
        this.height = height;
      }
    }
  }

  ngOnDestroy(): void {
    this.yp.unregister(this.player);
  }
}
